/**
 * The handle the stepped Import & Run wizard drives.
 *
 * It holds what has to survive between steps — the run id, the file, and the
 * batch id Import stamps — and dispatches DataHubConsoleSlice thunks for the
 * work itself. No request is made from here.
 */
import type { RunSession } from "./api";
import {
  closeDataHubRun,
  openDataHubRun,
  recordDataHubRunStep,
  runDataHubRunPhase,
} from "../../Store/Reducers/DataHubConsoleSlice";
import type { AppDispatch } from "../../Store/Store";

/**
 * Re-bind an existing pipeline run so QC / Heal / Promote can continue after
 * a resume, without opening a second `pipeline_runs` row.
 */
export async function attachRunSession(
  dispatch: AppDispatch,
  runId: string,
  file: File | null,
  batchId?: string | number | null
): Promise<RunSession> {
  let currentBatchId: string | number | undefined = batchId ?? undefined;

  return {
    runId,
    batchId: () => currentBatchId,
    runPhase: async (phase, options) => {
      const result = await dispatch(
        runDataHubRunPhase({
          runId,
          phase,
          file,
          batchId: currentBatchId,
          plan: options?.plan ?? null,
        })
      ).unwrap();
      currentBatchId = result.batchId;
      return result.outcome;
    },
    recordStep: (step, detail) =>
      dispatch(recordDataHubRunStep({ runId, step, detail })).unwrap(),
    close: () => dispatch(closeDataHubRun(runId)).unwrap(),
  };
}

export async function openRunSession(
  dispatch: AppDispatch,
  file: File | null,
  source?: string
): Promise<RunSession> {
  const runId = await dispatch(openDataHubRun({ source })).unwrap();
  return attachRunSession(dispatch, runId, file);
}
